/**
 * What the school will say, and what we say back.
 *
 * The database has refused to endorse a claim carrying an unanswered fatal
 * objection since migration 0013, and `red_team_item` is read on the Room
 * screen, in the Ledgers table and in the plain-English version — but nothing
 * in the application had ever written a row, and until this morning nothing
 * could read one back with its id. So the rule that decides whether a claim may
 * be endorsed has been running on three seeded rows, and a representative who
 * thought of the objection that sinks a claim had nowhere to put it.
 *
 * Two writes, deliberately separate, because they are two different acts, and
 * the surface has to keep them separate or the distinction is lost:
 *
 * **Raising** is anticipating the employer. Cheap, uncommitted, no judgement
 * about how dangerous it is. One field.
 *
 * **Rating** is the group deciding how much trouble it is in. It is what the
 * gate reads, and it is what `plain.ts` filters on before showing an objection
 * to a hundred and twenty people — so an unrated objection says so on its face
 * rather than sitting in the list looking settled.
 *
 * The counter travels with the rating rather than saving on its own, because
 * `PATCH /objections/:id` has no rating-free form and should not: writing the
 * answer and declaring the objection answered are the same decision, and
 * splitting them into two buttons invites the state where an answer exists and
 * the claim is still blocked with nobody knowing why.
 */

import { useState, type CSSProperties } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, type ClaimDetail, type ClaimObjection, type RateOutcome } from '../lib/api.js';

const RATINGS = ['weak', 'answered', 'fatal'] as const;
type Rating = (typeof RATINGS)[number];

const RATING_LABELS: Record<Rating, string> = {
  weak: 'Weak',
  answered: 'Answered',
  fatal: 'Fatal',
};

/**
 * The statuses the fatal-objection gate guards, copied from migration 0013.
 *
 * Copied, and therefore not the authority — the trigger is, and it runs whether
 * or not this list is right. It is here so the workbench can say *before* the
 * status dropdown is touched that this claim will be refused, rather than
 * letting a representative discover it from a database error in the Ledgers
 * table two screens away.
 */
const ADVANCED = ['group_endorsed', 'tabled', 'employer_responded', 'agreed'];

/**
 * How many objections would stop this claim advancing, if it tried now.
 *
 * The trigger's own predicate, exactly: `rating = 'fatal' and (counter is null
 * or btrim(counter) = '')`. A whitespace-only counter is not an answer, and
 * treating it as one here would put a green light on a claim Postgres is about
 * to refuse.
 *
 * Null once the claim is already advanced, because the gate only fires on a
 * claim *entering* one of those statuses — telling someone a tabled claim
 * cannot advance is both untrue and unactionable.
 */
export function endorsementBlock(
  status: string,
  objections: readonly ClaimObjection[],
): number | null {
  if (ADVANCED.includes(status)) return null;
  const blocking = objections.filter(
    (o) => o.rating === 'fatal' && (o.counter === null || o.counter.trim() === ''),
  ).length;
  return blocking > 0 ? blocking : null;
}

/**
 * The one line at the top of the panel: how many, and how many nobody has weighed.
 *
 * An unrated count is worth its own clause because an unrated objection is
 * invisible to two other surfaces. It is not in the plain-English version, and
 * it cannot block a claim, so a panel showing "3 objections" over three rows
 * nobody has rated is describing work that has had no effect anywhere.
 */
export function objectionSummary(objections: readonly ClaimObjection[]): string {
  if (objections.length === 0) return 'none yet';
  const unrated = objections.filter((o) => !o.rated).length;
  const counted = `${objections.length} raised`;
  return unrated === 0 ? counted : `${counted} · ${unrated} not yet weighed`;
}

export function Objections({
  claim,
  isRep,
  onSaved,
}: {
  claim: ClaimDetail;
  isRep: boolean;
  onSaved: () => void;
}) {
  const [raising, setRaising] = useState(false);
  const blocking = endorsementBlock(claim.status, claim.objections);

  return (
    <section className="section-rule mt-7 pt-5">
      <div className="mb-2 flex flex-wrap items-baseline gap-3">
        <h2 className="label m-0">What the school will say</h2>
        <span className="label">{objectionSummary(claim.objections)}</span>
      </div>

      {blocking !== null ? (
        /*
          Said here, on the claim, rather than only when the status change is
          refused. The refusal arrives in the Ledgers table — a different
          screen, from a database error, naming a ref rather than a sentence —
          and by then the representative has already decided to endorse. The
          fact is a property of the claim, so it belongs on the claim.

          Ink rather than `--color-defect`: nothing is broken. The group rated
          an objection fatal and has not answered it, which is the discipline
          working exactly as designed.
        */
        <div
          className="callout measure mb-3"
          style={{ '--callout-hue': 'var(--color-ink)' } as CSSProperties}
        >
          <span className="callout-glyph" aria-hidden>
            ◔
          </span>
          <div>
            <p className="label m-0">
              This claim cannot be endorsed — {blocking} unanswered fatal objection
              {blocking === 1 ? '' : 's'}
            </p>
            <p className="mt-1 text-[12.5px] leading-relaxed">
              Write the answer below, or change the rating. The database refuses the status change
              either way, so nothing is lost by leaving it as it stands.
            </p>
          </div>
        </div>
      ) : null}

      {claim.objections.length === 0 ? (
        <p className="measure text-[12.5px] leading-relaxed text-[var(--color-muted)]">
          Nothing raised yet. The objection worth writing down is the one you would struggle to
          answer across a table — a claim that has survived it in here is a claim that survives it
          in the room.
        </p>
      ) : (
        <div className="card">
          {claim.objections.map((objection) => (
            <ObjectionRow
              key={objection.id}
              objection={objection}
              isRep={isRep}
              onSaved={onSaved}
            />
          ))}
        </div>
      )}

      {isRep ? (
        raising ? (
          <RaiseObjection
            claimId={claim.id}
            onDone={() => {
              setRaising(false);
              onSaved();
            }}
            onCancel={() => setRaising(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setRaising(true)}
            className="hit label mt-2 underline underline-offset-2"
          >
            Raise an objection
          </button>
        )
      ) : null}
    </section>
  );
}

function ObjectionRow({
  objection,
  isRep,
  onSaved,
}: {
  objection: ClaimObjection;
  isRep: boolean;
  onSaved: () => void;
}) {
  const [counter, setCounter] = useState(objection.counter ?? '');
  const [editing, setEditing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const rate = useMutation<RateOutcome, Error, Rating>({
    // The counter goes with every rating, not only with "answered". A rep who
    // writes the answer and then rates it weak has still written the answer,
    // and dropping it would silently lose two sentences of work.
    mutationFn: (rating) =>
      api.rateObjection(objection.id, {
        rating,
        ...(counter.trim() ? { counter: counter.trim() } : {}),
      }),
    onSuccess: (result) => {
      if (result.kind === 'refused') {
        // Not an error state. The editor opens so the thing that would fix it
        // is the next thing under the cursor.
        setRefusal(result.reason);
        setEditing(true);
        return;
      }
      setRefusal(null);
      setEditing(false);
      onSaved();
    },
  });

  return (
    <div className="border-b border-[var(--color-rule)] py-3 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="label m-0">Employer says</p>
        {objection.draftedBy === 'ai' && !objection.rated ? (
          // Provenance survives here for the same reason it does on a claim's
          // wording: an objection nobody has weighed, printed under "what the
          // school will say", teaches people to expect an argument that may
          // never come.
          <span className="stamp stamp-ai">drafted by software · nobody has weighed it</span>
        ) : null}
        {!objection.rated ? (
          <span className="label ml-auto">not in the plain version until rated</span>
        ) : null}
      </div>
      <p className="register-reading measure mt-1 text-[14px] leading-relaxed">
        {objection.objection}
      </p>

      <p className="label mt-3">We answer</p>
      {editing ? (
        <>
          <label htmlFor={`counter-${objection.id}`} className="sr-only">
            Our answer to this objection
          </label>
          <textarea
            id={`counter-${objection.id}`}
            autoFocus
            rows={3}
            value={counter}
            onChange={(event) => setCounter(event.target.value)}
            placeholder="What we say back, in two or three sentences."
            className="measure mt-1 w-full rounded-[6px] border border-[var(--color-rule-strong)] px-2.5 py-2 text-[14px] outline-none"
            style={{ fontFamily: 'var(--font-serif)' }}
          />
        </>
      ) : objection.counter ? (
        <p className="register-reading measure mt-1 text-[14px] leading-relaxed">
          {objection.counter}
        </p>
      ) : (
        <p className="mt-1 text-[12.5px] text-[var(--color-muted)]">
          Nobody has answered this yet.
        </p>
      )}

      {isRep ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
          {!editing ? (
            <button
              type="button"
              // Re-seeded on open, not once on mount. The row is keyed by id so
              // it never remounts, and a colleague who answered this objection
              // while the claim was open would otherwise be overwritten by a
              // draft seeded from what the counter said an hour ago.
              onClick={() => {
                setCounter(objection.counter ?? '');
                setEditing(true);
              }}
              className="hit label underline underline-offset-2"
            >
              {objection.counter ? 'Edit the answer' : 'Write the answer'}
            </button>
          ) : null}

          <span className="label">Rate it</span>
          {/*
            Three buttons rather than a select, because the rating is the act
            that moves the claim and a dropdown makes it look like metadata.
            aria-pressed rather than a toggle, since these are three states of
            one setting and only one can hold at a time.
          */}
          {RATINGS.map((rating) => {
            const on = objection.rating === rating;
            return (
              <button
                key={rating}
                type="button"
                aria-pressed={on}
                disabled={rate.isPending}
                onClick={() => rate.mutate(rating)}
                className={`hit pill label disabled:opacity-40 ${on ? 'pill-active' : ''}`}
              >
                {RATING_LABELS[rating]}
              </button>
            );
          })}

          {editing ? (
            <button
              type="button"
              onClick={() => {
                setCounter(objection.counter ?? '');
                setEditing(false);
                setRefusal(null);
              }}
              className="hit label underline underline-offset-2"
            >
              Cancel
            </button>
          ) : null}
        </div>
      ) : null}

      {rate.error ? (
        // A genuine fault, and it does wear the defect colour — the whole point
        // of carrying the 422 as a value is that these two look different.
        <p role="alert" className="mt-2 text-[12px] text-[var(--color-defect)]">
          {rate.error.message}
        </p>
      ) : null}

      {refusal ? (
        /*
          The server's 422, rendered as the decision it is.

          It is the rating that unblocks the claim, so "answered" with nothing
          written would let anyone clear the gate by relabelling the problem.
          In red beside the word "error" that reads as a bug and teaches a rep
          to press the button again; framed as a sentence about what would have
          to be true, it sends them to the textarea directly above, which this
          has already opened.
        */
        <div
          className="callout measure mt-2"
          style={{ '--callout-hue': 'var(--color-ink)' } as CSSProperties}
        >
          <span className="callout-glyph" aria-hidden>
            ◔
          </span>
          <div>
            <p className="label m-0">Not rated answered — the answer is still blank</p>
            <p className="mt-1 text-[12.5px] leading-relaxed">{refusal}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RaiseObjection({
  claimId,
  onDone,
  onCancel,
}: {
  claimId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');

  const raise = useMutation({
    mutationFn: () => api.raiseObjection(claimId, { objection: text.trim() }),
    onSuccess: onDone,
  });

  return (
    <form
      className="card measure mt-3"
      onSubmit={(event) => {
        event.preventDefault();
        // 10 characters is the route's own floor. Enforced here too so the
        // failure is a disabled button rather than a 400 with a sentence about
        // how the school would put it.
        if (text.trim().length >= 10) raise.mutate();
      }}
    >
      <div className="card-header">
        <p className="label m-0">Raise an objection</p>
        <span className="label">unrated until the group weighs it</span>
      </div>

      <label htmlFor={`raise-objection-${claimId}`} className="sr-only">
        The objection, as the school would put it
      </label>
      <textarea
        id={`raise-objection-${claimId}`}
        autoFocus
        rows={3}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Write it as the school would put it, in a sentence or two."
        className="w-full rounded-[6px] border border-[var(--color-rule-strong)] px-2.5 py-2 text-[14px] outline-none"
        style={{ fontFamily: 'var(--font-serif)' }}
      />

      {raise.error ? (
        <p role="alert" className="mt-2 text-[12px] text-[var(--color-defect)]">
          {raise.error.message}
        </p>
      ) : null}

      <div className="mt-2.5 flex items-center gap-3">
        <button
          type="submit"
          disabled={raise.isPending || text.trim().length < 10}
          className="hit shrink-0 rounded-[6px] px-3 py-1.5 text-[12.5px] disabled:opacity-40"
          style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
        >
          {raise.isPending ? 'Raising…' : 'Raise it'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="hit label underline underline-offset-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
